import { AUTO_CHECK_SCORE_THRESHOLD } from "@nlc/shared-types";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal/ConfirmationModal";

import type { FileResult } from "../types";

import { sortChecksBySeverity } from "../utils/sortChecksBySeverity";
import { DiagnosticCheckItem } from "./DiagnosticCheckItem";

interface ConfidenceScorePanelProps {
  onOverrideScore: () => void;
  onReplaceFile: () => void;
  result: FileResult | null;
}

export function ConfidenceScorePanel({
  onOverrideScore,
  onReplaceFile,
  result,
}: ConfidenceScorePanelProps) {
  const [isOverrideConfirmOpen, setIsOverrideConfirmOpen] = useState(false);

  const isAutoChecked = result?.tag === "auto_checked";
  const isOverridden = result?.tag === "overridden";
  const isFailed = result?.tag === "failed";

  const handleOverrideConfirm = () => {
    setIsOverrideConfirmOpen(false);
    onOverrideScore();
  };

  return (
    <div className="flex w-[500px] shrink-0 flex-col border-l border-border-secondary">
      <div className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-quaternary">
            Confidence Score
          </span>
          {result?.status === "done" && (
            <span
              className={twMerge(
                "text-2xl font-bold",
                scoreColorClass(result.score),
              )}
            >
              {result.score}%
            </span>
          )}
        </div>

        <div className="relative w-full">
          <div className="h-2 w-full overflow-hidden rounded-full bg-bg-secondary">
            {result?.status === "done" ? (
              <div
                className={twMerge(
                  "h-full rounded-full transition-all duration-700",
                  scoreBarClass(result.score),
                )}
                style={{ width: `${result.score}%` }}
              />
            ) : (
              <div className="h-full w-full animate-pulse rounded-full bg-bg-secondary" />
            )}
          </div>
          <div
            className="absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-border-primary"
            style={{ left: `${AUTO_CHECK_SCORE_THRESHOLD}%` }}
            title={`Auto-check threshold (${AUTO_CHECK_SCORE_THRESHOLD}%)`}
          />
        </div>

        <div className="border-t border-border-secondary p-5">
          {isAutoChecked ? (
            <div className="flex items-center justify-center rounded-md border border-border-success bg-bg-success-secondary px-4 py-2.5">
              <span className="text-sm font-semibold text-text-success-primary">
                Auto-checked
              </span>
            </div>
          ) : isOverridden ? (
            <div className="flex items-center justify-center rounded-md border border-border-secondary bg-bg-secondary px-4 py-2.5">
              <span className="text-sm font-semibold text-text-secondary">
                Overridden
              </span>
            </div>
          ) : isFailed ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-center rounded-md border border-border-error bg-bg-error-secondary px-4 py-2.5">
                <span className="text-sm font-semibold text-text-error-primary">
                  AI Check Failed
                </span>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                className="flex-1"
                color="gray"
                onPress={() => setIsOverrideConfirmOpen(true)}
                size="md"
                variant="secondary"
              >
                Override score
              </Button>
              <Button
                className="flex-1"
                color="color"
                onPress={onReplaceFile}
                size="md"
                variant="primary"
              >
                <RefreshCw size={14} />
                Replace file
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-border-secondary" />
          <span className="text-sm font-medium text-text-primary whitespace-nowrap">
            Diagnostic checks
          </span>
          <div className="flex-1 border-t border-border-secondary" />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-5 pb-4">
        {result?.status === "done"
          ? sortChecksBySeverity(result.checks).map((check) => (
              <DiagnosticCheckItem check={check} key={check.id} />
            ))
          : null}
      </div>

      <ConfirmationModal
        cancelButtonText="Cancel"
        confirmButtonColor="color"
        confirmButtonText="Confirm override"
        icon={<AlertTriangle className="text-text-warning-primary" size={20} />}
        iconBackgroundClassName="bg-bg-warning-secondary"
        isOpen={isOverrideConfirmOpen}
        onConfirm={handleOverrideConfirm}
        onOpenChange={setIsOverrideConfirmOpen}
        text="Are you sure you want to override the score and submit the design file with this criteria?"
        title="Override AI score?"
      />
    </div>
  );
}

function scoreColorClass(score: number): string {
  if (score >= 85) return "text-text-success-primary";
  if (score >= 65) return "text-[#F79009]";

  return "text-text-error-primary";
}

function scoreBarClass(score: number): string {
  if (score >= 85) return "bg-bg-success-solid";
  if (score >= 65) return "bg-[#F79009]";

  return "bg-bg-error-solid";
}
